import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Button,
  Tooltip,
  CircularProgress,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface ImageLightboxProps {
  open: boolean;
  images: string[];          // array of image URLs
  onClose: () => void;
  label?: string | null;     // optional main label/caption for all images
  captions?: (string | null)[]; // optional captions for each image
  initialIndex?: number;     // optional initial index
  showThumbnails?: boolean;  // show clickable thumbnails below main image
  sx?: React.CSSProperties;  // custom styles for dialog content container
}

const Transition = React.forwardRef(function Transition(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: any,
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  open,
  images,
  onClose,
  label,
  captions = [],
  initialIndex = 0,
  showThumbnails = true,
  sx,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [loading, setLoading] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Reset index when open or initialIndex changes
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, open]);

  // Preload current image
  useEffect(() => {
    if (!open || images.length === 0) return;
    setLoading(true);
    const img = new Image();
    img.src = images[currentIndex];
    img.onload = () => setLoading(false);
  }, [currentIndex, images, open]);

  // Keyboard navigation: arrows for next/prev, ESC for close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex((i) => (i + 1) % images.length);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentIndex((i) => (i - 1 + images.length) % images.length);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [open, images.length, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (images.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Transition}
      aria-labelledby="lightbox-title"
      aria-describedby="lightbox-description"
      scroll="body"
      PaperProps={{
        sx: {
          backgroundColor: '#fff',
          color: '#fff',
          overflow: 'hidden',
          maxHeight: '90vh',
          ...sx,
        },
      }}
      ref={dialogRef}
    >
      <DialogTitle
        id="lightbox-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fff',
          color: '#222',
          fontWeight: 'bold',
          fontSize: '1.1rem',
          userSelect: 'none',
          px: 2,
          py: 1,
        }}
      >
        <div>
          {label ? label : 'Image'} {currentIndex + 1} / {images.length}
        </div>
        <IconButton
          onClick={onClose}
          aria-label="Close lightbox"
          sx={{
            color: '#ccc',
            '&:hover': { color: '#fff' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: 300,
          userSelect: 'none',
          px: 0,
          py: 1,
        }}
        aria-describedby="lightbox-description"
      >
        {loading ? (
          <CircularProgress
            color="inherit"
            size={48}
            sx={{ mt: 6, mb: 6 }}
            aria-label="Loading image"
          />
        ) : (
          <>
            {/* Previous Button */}
            {images.length > 1 && (
              <Tooltip title="Previous image">
                <IconButton
                  onClick={() =>
                    setCurrentIndex(
                      (idx) => (idx - 1 + images.length) % images.length
                    )
                  }
                  aria-label="Previous image"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 8,
                    transform: 'translateY(-50%)',
                    color: '#fff',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    zIndex: 10,
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Main Image */}
            <img
              src={images[currentIndex]}
              alt={captions[currentIndex] || label || `Image ${currentIndex + 1}`}
              loading="lazy"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                borderRadius: 4,
                boxShadow: '0 0 16px rgba(0,0,0,0.8)',
                userSelect: 'none',
              }}
              draggable={false}
            />

            {/* Next Button */}
            {images.length > 1 && (
              <Tooltip title="Next image">
                <IconButton
                  onClick={() =>
                    setCurrentIndex((idx) => (idx + 1) % images.length)
                  }
                  aria-label="Next image"
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: 8,
                    transform: 'translateY(-50%)',
                    color: '#fff',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    zIndex: 10,
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Caption */}
            {captions[currentIndex] && (
              <figcaption
                id="lightbox-description"
                style={{
                  marginTop: 12,
                  color: '#ddd',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  maxWidth: '90%',
                  userSelect: 'text',
                }}
              >
                {captions[currentIndex]}
              </figcaption>
            )}
          </>
        )}
      </DialogContent>

      {/* Thumbnails */}
      {showThumbnails && images.length > 1 && (
        <DialogActions
          sx={{
            justifyContent: 'center',
            bgcolor: '#fff',
            overflowX: 'auto',
            px: 1,
            py: 1,
            gap: 1,
          }}
        >
          {images.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt={`Thumbnail ${i + 1}`}
              loading="lazy"
              onClick={() => setCurrentIndex(i)}
              style={{
                width: 30,
                height: 30,
                objectFit: 'cover',
                borderRadius: 4,
                cursor: 'pointer',
                border:
                  i === currentIndex ? '2px solid #2196f3' : '2px solid transparent',
                opacity: i === currentIndex ? 1 : 0.6,
                transition: 'opacity 0.25s, border-color 0.25s',
                userSelect: 'none',
                flexShrink: 0,
              }}
              draggable={false}
            />
          ))}
        </DialogActions>
      )}

      {/* Close Button at bottom for easier mobile access */}
      <DialogActions
        sx={{
          justifyContent: 'center',
          bgcolor: '#fff',
          py: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          aria-label="Close lightbox"
          sx={{ minWidth: 100 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageLightbox;
